import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleQuestion,
  faCircleXmark,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useId, useState } from 'react';

import type {
  TreatabilityDetermination,
  TreatabilityExplanation,
  TreatabilityRequirement
} from '@/types/insurancePolicy';

import './styles.css';
import type { Props, RequirementGroup } from './types';

const TONES = {
  treatable: {
    title: 'Treatable',
    icon: faCircleCheck,
    surface: 'bg-green-50',
    border: 'border-green-200',
    iconClassName: 'text-green-600',
    titleClassName: 'text-green-800'
  },
  'not treatable': {
    title: 'Not treatable',
    icon: faCircleXmark,
    surface: 'bg-red-50',
    border: 'border-red-200',
    iconClassName: 'text-red-600',
    titleClassName: 'text-red-800'
  },
  waitlisted: {
    title: 'Waitlisted',
    icon: faClock,
    surface: 'bg-yellow-50',
    border: 'border-yellow-200',
    iconClassName: 'text-yellow-600',
    titleClassName: 'text-yellow-800'
  },
  unknown: {
    title: 'Treatability unknown',
    icon: faCircleQuestion,
    surface: 'bg-uniform-gray-50',
    border: 'border-uniform-gray-200',
    iconClassName: 'text-uniform-gray-400',
    titleClassName: 'text-uniform-gray-800'
  }
} as const;

const REQUIREMENT_GROUPS: RequirementGroup[] = [
  {
    key: 'failing',
    title: 'Failing',
    results: ['negative'],
    mark: '✕',
    markClassName: 'text-red-600',
    titleClassName: 'text-red-700',
    countClassName: 'bg-red-100 text-red-800'
  },
  {
    key: 'not-checked',
    title: 'Not checked',
    results: ['unknown'],
    mark: '—',
    markClassName: 'text-uniform-gray-400',
    titleClassName: 'text-uniform-gray-700',
    countClassName: 'bg-uniform-gray-100 text-uniform-gray-700'
  },
  {
    key: 'passing',
    title: 'Passing',
    results: ['affirmative'],
    mark: '✓',
    markClassName: 'text-green-600',
    titleClassName: 'text-green-800',
    countClassName: 'bg-green-100 text-green-800'
  },
  {
    key: 'not-required',
    title: 'Not required',
    results: ['not_applicable'],
    mark: '○',
    markClassName: 'text-uniform-gray-400',
    titleClassName: 'text-uniform-gray-700',
    countClassName: 'bg-uniform-gray-100 text-uniform-gray-700'
  }
];

const toneFor = (result: TreatabilityDetermination['result'] | undefined) =>
  (result && TONES[result]) || TONES.unknown;

const countWith = (
  requirements: TreatabilityRequirement[],
  results: TreatabilityRequirement['result'][]
) => requirements.filter(({ result }) => results.includes(result)).length;

export const requirementsSummary = (requirements: TreatabilityRequirement[]) => {
  const total = requirements.length;
  if (total === 0) return null;

  const failing = countWith(requirements, ['negative']);
  if (failing > 0) return `${failing} of ${total} requirements failing`;

  const notChecked = countWith(requirements, ['unknown']);
  if (notChecked > 0) return `${notChecked} of ${total} requirements not checked`;

  return `All ${total} requirements met`;
};

const SectionHeading = ({
  children,
  className = 'text-uniform-gray-600'
}: {
  children: React.ReactNode;
  className?: string;
}) => <h4 className={`text-xs font-semibold uppercase tracking-wide ${className}`}>{children}</h4>;

const activeOnLabel = (activeOn: string | null | undefined) =>
  activeOn ? new Date(`${activeOn}T00:00:00`).toLocaleDateString('en-US') : null;

const Details = ({ explanation }: { explanation: TreatabilityExplanation }) => {
  const { determination, requirements, prerequisites } = explanation;
  const activeOn = activeOnLabel(determination.treatability_match_active_on);

  return (
    <div className="flex flex-col gap-5 text-sm">
      {determination.description && (
        <p className="text-uniform-gray-800">{determination.description}</p>
      )}

      {prerequisites.length > 0 && (
        <section aria-label="Evaluated for" className="flex flex-col gap-2">
          <SectionHeading>Evaluated for</SectionHeading>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-md bg-uniform-gray-50 px-3.5 py-3">
            {prerequisites.map((prerequisite) => (
              <div key={prerequisite.label} className="flex min-w-0 flex-col gap-0.5">
                <dt className="text-xs text-uniform-gray-500">{prerequisite.label}</dt>
                <dd
                  className={`font-medium ${prerequisite.result ? 'text-uniform-gray-900' : 'text-red-700'}`}>
                  {prerequisite.value ?? 'Not set'}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {determination.treatability_match && (
        <section aria-label="Matched rule" className="flex flex-col gap-2">
          <SectionHeading>Matched rule</SectionHeading>
          <div className="flex flex-col gap-0.5 rounded-md border border-uniform-gray-200 px-3.5 py-2.5">
            <span className="font-medium text-uniform-gray-900">
              {determination.treatability_match}
            </span>
            {activeOn && <span className="text-uniform-gray-600">{`Active on ${activeOn}`}</span>}
          </div>
        </section>
      )}

      {REQUIREMENT_GROUPS.map((group) => {
        const members = requirements.filter(({ result }) => group.results.includes(result));
        if (members.length === 0) return null;

        return (
          <section key={group.key} aria-label={group.title} className="flex flex-col">
            <div className="flex items-center gap-2 border-b border-uniform-gray-200 pb-2">
              <SectionHeading className={group.titleClassName}>{group.title}</SectionHeading>
              <span
                className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold ${group.countClassName}`}>
                {members.length}
              </span>
            </div>
            <ul className="divide-y divide-uniform-gray-200">
              {members.map((requirement) => (
                <li key={requirement.label} className="flex gap-3 py-2.5">
                  <span
                    aria-hidden="true"
                    className={`w-3 shrink-0 font-semibold ${group.markClassName}`}>
                    {group.mark}
                  </span>
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="font-medium text-uniform-gray-900">{requirement.label}</span>
                    {requirement.description && (
                      <span className="text-uniform-gray-600">{requirement.description}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

const TreatabilityBanner = ({ explanation }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const detailsId = useId();
  const tone = toneFor(explanation?.determination.result);
  const summary = explanation ? requirementsSummary(explanation.requirements) : null;

  return (
    <section aria-label="Treatability" className={`rounded-t-[7px] border-b ${tone.border}`}>
      <div className={`flex items-center gap-3 rounded-t-[7px] px-4 py-3 ${tone.surface}`}>
        <FontAwesomeIcon
          icon={tone.icon}
          className={`h-6 w-6 shrink-0 ${tone.iconClassName}`}
          data-testid="treatability-icon"
        />
        <div className="flex min-w-0 grow flex-col">
          <span className={`text-base font-semibold ${tone.titleClassName}`}>{tone.title}</span>
          {summary && <span className="text-sm text-uniform-gray-700">{summary}</span>}
        </div>
        {explanation && (
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={detailsId}
            onClick={() => setIsOpen((open) => !open)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded px-1 text-sm font-medium text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-uniform-gray-400">
            {isOpen ? 'Hide details' : 'View details'}
            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="h-3 w-3" />
          </button>
        )}
      </div>
      {explanation && isOpen && (
        <div id={detailsId} className={`border-t bg-white px-4 py-4 ${tone.border}`}>
          <Details explanation={explanation} />
        </div>
      )}
    </section>
  );
};

export default React.memo(TreatabilityBanner);
